import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Application } from '../types';
import { apiService } from '../services/api';
import { storage } from '../utils/localStorage';
import { setAppearanceSchema } from '../utils/appearanceSchema';
import { appScreenPath } from '../utils/screenPath';

const APPS_CACHE_KEY = 'applicationsCache';
const APPS_REVISION_KEY = 'applicationsRevision';

export const useApplications = (isAuthenticated: boolean) => {
    const navigate = useNavigate();
    const params = useParams();
    const appId = params.appId;

    const [applications, setApplications] = useState<Application[]>([]);
    const [selectedApplication, setSelectedApplication] = useState<Application | null>(null);
    const [loadingApplications, setLoadingApplications] = useState(false);

    // Revision of the list this instance shows. Not read from storage when
    // comparing: another tab may already have saved a newer list there.
    const shownRevision = useRef<string | null>(null);
    const checkingRevision = useRef(false);

    // silent: refresh in place (e.g. after a widget edit) without the full-page loader
    // revision: the server revision when the caller has just read it
    const loadApplications = useCallback(async ({silent = false, revision}: {silent?: boolean; revision?: string | null} = {}) => {
        if (!silent) setLoadingApplications(true);
        try {
            // Read before the list, never alongside it: a change landing in
            // between then leaves an older revision next to newer data (one
            // spare reload) instead of old data marked as current.
            const listRevision = revision !== undefined ? revision : await apiService.getApplicationsRevision();
            const [appsResponse, operatorsResponse, appearanceResponse] = await Promise.all([
                apiService.getApplications(),
                apiService.getFilterOperators(),
                apiService.getAppearanceSchema()
            ]);

            if (operatorsResponse.success && operatorsResponse.data) {
                const operatorsData = operatorsResponse.data.data || operatorsResponse.data;
                storage.setItem('filterOperators', JSON.stringify(operatorsData));
            }

            if (appearanceResponse.success && appearanceResponse.data) {
                // request() hands back the whole body; the schema sits under its data key
                const body = appearanceResponse.data as any;
                setAppearanceSchema(body.data ?? body);
            }

            if (appsResponse.success) {
                const apps = Array.isArray(appsResponse.data) ? appsResponse.data : (appsResponse.data as any).data;

                if (!Array.isArray(apps)) {
                    return;
                }

                const fullApps: Application[] = apps.map((app: any) => ({
                    id: app.id,
                    name: app.displayName || app.name,
                    displayName: app.displayName,
                    code: app.code,
                    project: app.project,
                    url: app.url,
                    description: app.description,
                    defaultScreenId: app.defaultScreenId,
                    screens: app.screens,
                    sortOrder: app.sortOrder,
                }));

                setApplications(fullApps);
                shownRevision.current = listRevision;

                try {
                    storage.setItem(APPS_CACHE_KEY, JSON.stringify(fullApps));
                    if (listRevision) {
                        storage.setItem(APPS_REVISION_KEY, listRevision);
                    } else {
                        storage.removeItem(APPS_REVISION_KEY);
                    }
                } catch {
                    // localStorage full — no cache, the next mount just fetches
                }
            }
        } catch (error) {
        } finally {
            if (!silent) setLoadingApplications(false);
        }
        // No appId dependency on purpose: it would recreate this callback on
        // every app navigation and the effect below would refetch /applications
        // each time. Selection from the URL is handled by its own effect.
    }, []);

    // Refetches the list only when the server's revision differs from the one
    // of the list on screen.
    const refreshIfChanged = useCallback(async () => {
        if (checkingRevision.current) return;
        checkingRevision.current = true;
        try {
            const revision = await apiService.getApplicationsRevision();
            if (revision && revision !== shownRevision.current) {
                await loadApplications({silent: true, revision});
            }
        } finally {
            checkingRevision.current = false;
        }
    }, [loadApplications]);

    useEffect(() => {
        if (!isAuthenticated) return;

        // A page reload (F5) shows the cached list at once and downloads it
        // again only when the server reports a different revision; otherwise
        // the full fetch happens on login (cache cleared there) and on the
        // logo click, which calls loadApplications() directly.
        const cached = storage.getItem(APPS_CACHE_KEY);
        if (cached) {
            try {
                const apps = JSON.parse(cached);
                if (Array.isArray(apps) && apps.length > 0) {
                    setApplications(apps);
                    shownRevision.current = storage.getItem(APPS_REVISION_KEY);
                    refreshIfChanged();
                    return;
                }
            } catch {
                // corrupt cache — fall through to a fresh fetch
            }
        }

        loadApplications();
    }, [isAuthenticated, loadApplications, refreshIfChanged]);

    // Coming back to the tab or window, e.g. from the admin.
    useEffect(() => {
        if (!isAuthenticated) return;

        const onReturn = () => {
            if (document.visibilityState === 'visible') refreshIfChanged();
        };
        document.addEventListener('visibilitychange', onReturn);
        window.addEventListener('focus', onReturn);
        return () => {
            document.removeEventListener('visibilitychange', onReturn);
            window.removeEventListener('focus', onReturn);
        };
    }, [isAuthenticated, refreshIfChanged]);

    useEffect(() => {
        if (appId && applications.length > 0) {
            const app = applications.find(a => String(a.id) === appId);
            // By identity: a reloaded list carries new objects for the same application.
            if (app && selectedApplication !== app) {
                setSelectedApplication(app);
            }
        }
    }, [appId, applications, selectedApplication]);

    const handleApplicationSelect = (app: Application) => {
        setSelectedApplication(app);
        const defaultScreenId = app.defaultScreenId || app.screens?.[0]?.id;
        if (defaultScreenId) {
            navigate(appScreenPath(app, defaultScreenId));
        }
    };

    const handleLogoClick = async () => {
        setSelectedApplication(null);
        await loadApplications();
        navigate('/');
    };

    return {
        applications,
        selectedApplication,
        setSelectedApplication,
        loadingApplications,
        handleApplicationSelect,
        handleLogoClick,
        loadApplications
    };
};
